<template>
  <v-form ref="form" v-model="valid" lazy-validation class="full">
    <v-col class="md-hidden pager-row">
      <span class="back-button" @click="goBack">
        <i class="fas fa-chevron-left m-r-5"></i>
        Go Back
      </span>
      <radial-progress-bar
        :diameter="45"
        :completed-steps="completedSteps"
        :total-steps="totalSteps"
        :startColor="'#00A8C8'"
        :stopColor="'#00A8C8'"
        :innerStrokeColor="'#B0E5EF'"
        :strokeWidth="3"
        :innerStrokeWidth="3"
        class="right progress-bar"
      >
        <p>
          <span>{{ completedSteps }}</span> of {{ totalSteps }}
        </p>
      </radial-progress-bar>
    </v-col>
    <div class="form-body col-md-8 col-12">
      <v-col cols="12" sm="12" md="12" class="header-div">
        <h3>Claims Record</h3>
      </v-col>

      <v-row class="form-item-section">
        <v-col cols="12" sm="12" md="12" class="input-wrapper">
          <v-list-item-title class="m-b-20">
            Any accidents, losses or insurance claims during the past five years
            in connection with any yacht sailed or owned?
            <span class="red-star">*</span>
          </v-list-item-title>
          <v-radio-group
            v-model="claimRecord.is_exist_claim"
            :rules="[rules.required]"
            @change="clearAttachment"
          >
            <v-radio value="Y">
              <template v-slot:label>
                <span class="m-l-5">Yes</span>
              </template>
            </v-radio>
            <v-radio value="N">
              <template v-slot:label>
                <span class="m-l-5">No</span>
              </template>
            </v-radio>
          </v-radio-group>
        </v-col>
        <v-col
          cols="12"
          sm="12"
          md="12"
          class="input-wrapper"
          :hidden="claimRecord.is_exist_claim !== 'Y'"
        >
          <v-list-item-title>
            Details on accidents, losses or insurance claims <span class="red-star">*</span>
          </v-list-item-title>
          <v-textarea
            v-model="claimRecord.is_exist_claim_desc"
            color="primary"
            solo
            outlined
            flat
            dense
            :rules="claimRecord.is_exist_claim === 'Y' ? [rules.required] : []"
            :validate-on-blur="true"
            maxLength="200"
          />
        </v-col>

        <v-col cols="12" sm="12" md="12" class="input-wrapper">
          <v-list-item-title class="m-b-20">
            Have you had any yacht insurance declined or cancelled?
            <span class="red-star">*</span>
          </v-list-item-title>
          <v-radio-group
            v-model="claimRecord.is_insurance_declined"
            :rules="[rules.required]"
            @change="changeTextBox"
          >
            <v-radio value="N">
              <template v-slot:label>
                <span class="m-l-5">No</span>
              </template>
            </v-radio>
            <v-radio value="Y-D">
              <template v-slot:label>
                <span class="m-l-5">Yes, declined</span>
              </template>
            </v-radio>
            <v-radio value="Y-C">
              <template v-slot:label>
                <span class="m-l-5">Yes, cancelled</span>
              </template>
            </v-radio>
          </v-radio-group>
        </v-col>

        <v-col
          cols="12"
          sm="12"
          md="12"
          class="input-wrapper"
          :hidden="claimRecord.is_insurance_declined !== 'Y-D'"
        >
          <v-list-item-title>
            Reason for declined insurance <span class="red-star">*</span>
          </v-list-item-title>
          <v-textarea
            v-model="claimRecord.declined_reason"
            color="primary"
            solo
            outlined
            flat
            dense
            :rules="
              claimRecord.is_insurance_declined === 'Y-D'
                ? [rules.required]
                : []
            "
            :validate-on-blur="true"
            maxLength="150"
          />
        </v-col>
        <v-col
          cols="12"
          sm="12"
          md="12"
          class="input-wrapper"
          :hidden="claimRecord.is_insurance_declined !== 'Y-C'"
        >
          <v-list-item-title>
            Reason for cancellation <span class="red-star">*</span>
          </v-list-item-title>
          <v-textarea
            v-model="claimRecord.cancel_reason"
            color="primary"
            solo
            outlined
            flat
            dense
            :rules="
              claimRecord.is_insurance_declined === 'Y-C'
                ? [rules.required]
                : []
            "
            :validate-on-blur="true"
            maxLength="150"
          />
        </v-col>

        <v-col cols="12" sm="12" md="12" class="input-wrapper m-t-25" 
          v-if="claimRecord.is_exist_claim === 'Y'">
          <v-list-item-title>
             Please upload claims records or documents <span class="red-star">*</span>
          </v-list-item-title>
          <file-upload
            :post-action="'/upload/post'"
            :put-action="'/upload/put'"
            :accept="
              'image/png,image/jpeg,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            "
            :directory="false"
            :headers="headers"
            :data="data"
            :drop="false"
            :drop-directory="false"
            :add-index="false"
            v-model="claimRecord.attachment"
            :maximum="1"
            @input-filter="inputFilter"
            @input-file="inputFile"
            ref="uploadPhotos"
            input-id="uploadPhoto"
            class="add-photo-button"
          >
            <v-btn class="ma-2 form-button upload-button" dark outlined>
              <span class="upload-icon"></span>
              Upload
            </v-btn>
          </file-upload>
          <v-input
            v-if="claimRecord.attachment.length === 0"
            v-model="claimRecord.attachment"
            :rules="[rules.fileSize]"
          ></v-input>
        </v-col>

        <v-col
          cols="12"
          sm="12"
          md="12"
          class="input-wrapper"
          style="margin-top: 12px;"
          v-if="claimRecord.attachment.length > 0"
        >
          <div class="upload">
            <v-col
              style="overflow-x: auto;background: #F3F3F3;"
              class="xs-12 md-12 py-2 ma-4"
            >
              <div
                v-for="item in claimRecord.attachment.slice(0, 5)"
                v-bind:key="item.name"
                style="position: relative;"
              >
                <span>&#9679; {{ item.name }}</span>
                <span
                  class="delete-icon"
                  @click="showDeletePopup(claimRecord.attachment, item)"
                ></span>
              </div>
            </v-col>
          </div>
        </v-col>

        <v-dialog v-model="dialog" persistent class="vue-dialog">
          <v-card class="info-dialog">
            <div class="body">
              Are you sure you want to delete this attachment?
            </div>
            <div class="footer-section">
              <button class="cancel xs-hidden left" @click="dialog = !dialog">
                Cancel
              </button>
              <button @click="removeItem()" class="right">
                Ok
              </button>
              <button class="cancel md-hidden" @click="dialog = !dialog">
                Cancel
              </button>
            </div>
          </v-card>
        </v-dialog>
      </v-row>
    </div>

    <v-col
      cols="12"
      class="button-container col-md-8"
      v-if="!$store.state.isEditMode"
    >
      <v-btn
        class="ma-2 form-button cancel"
        outlined
        color="black"
        @click="goBack"
      >
        <span class="back-icon"></span>
        Previous Step
      </v-btn>
      <v-btn
        class="ma-2 form-button continue right"
        dark
        color="#00A8C8"
        @click="validateAndGoNext"
      >
        Continue
        <span class="next-icon"></span>
      </v-btn>
    </v-col>
    <v-col
      cols="12"
      class="button-container col-md-8"
      v-if="$store.state.isEditMode"
    >
      <v-btn
        class="ma-2 form-button cancel"
        outlined
        color="black"
        @click="cancelChange"
      >
        <span class="back-icon"></span>
        Cancel Changes
      </v-btn>
      <v-btn
        class="ma-2 form-button continue right"
        dark
        color="#00A8C8"
        @click="applyChange"
      >
        Apply Changes
        <span class="next-icon"></span>
      </v-btn>
    </v-col>
  </v-form>
</template>

<script>
import Compressor from "compressorjs";
import FileUpload from "vue-upload-component";
import RadialProgressBar from "vue-radial-progress";
import PremiumApi from "@/plugins/axios";

export default {
  components: {
    FileUpload,
    RadialProgressBar,
  },
  data() {
    return {
      valid: false,
      headers: {
        "X-Csrf-Token": "xxxx",
      },
      data: {
        _csrf_token: "xxxxxx",
      },
      dialog: false,
      autoCompress: 1024 * 1024,
      completedSteps: 6,
      totalSteps: 7,
      claimRecord: this.getClaimRecord(),
      rules: {
        required: (value) => !!value || "This field is mandatory",
        fileSize: (value) => this.claimRecord.attachment > 0 || "Please provide a copy of the report",
        numberRule: (value) => {
          const pattern = /^\d+$/;
          if (value == null || value == undefined) {
            return true;
          }
          return (
            pattern.test(
              String(value)
                .replace(/\,/g, "")
                .replace(/\-/g, "")
            ) || "This field must have numeric value"
          );
        },
        emailValidation: (value) => {
          var pattern = /^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;
          if (value == null || value === undefined || value === "") {
            return true;
          }
          return pattern.test(value) || "E-mail must be valid";
        },
      },
    };
  },
  methods: {
    isNumber: function(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    validateAndGoNext: function() {
      if (this.$refs.form.validate()) {
        this.$store.commit("saveClaimRecord", this.claimRecord);
        this.$store.state.formPageNumber = 7;
      } else {
        let context = this;
        setTimeout(function() {
          let inavlidField = context.$el.querySelector(".v-messages__message");
          if (inavlidField) {
            context.$vuetify.goTo(
              context.$el.querySelector(".v-messages__message").parentNode
                .parentNode.parentNode.parentNode.parentNode.parentNode,
              { container: document.documentElement, duration: 100 }
            );
            let focusElement = context.$el.querySelector(".v-messages__message")
              .parentNode.parentNode.parentNode.parentNode.parentNode
              .parentNode;
            focusElement.focus();
          }
        }, 100);
      }
    },
    goBack: function() {
      this.$store.state.formPageNumber = 5;
    },
    cancelChange: function() {
      this.$store.state.isEditMode = false;
      this.$store.state.formPageNumber = 7;
    },
    applyChange: function() {
      if (this.$refs.form.validate()) {
        this.$store.commit("saveClaimRecord", this.claimRecord);
        this.$store.state.isEditMode = false;
        this.$store.state.formPageNumber = 7;
      } else {
        let context = this;
        setTimeout(function() {
          let inavlidField = context.$el.querySelector(".v-messages__message");
          if (inavlidField) {
            context.$vuetify.goTo(
              context.$el.querySelector(".v-messages__message").parentNode
                .parentNode.parentNode.parentNode.parentNode.parentNode,
              { container: document.documentElement, duration: 100 }
            );
            let focusElement = context.$el.querySelector(".v-messages__message")
              .parentNode.parentNode.parentNode.parentNode.parentNode
              .parentNode;
            focusElement.focus();
          }
        }, 100);
      }
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (newFile.size === 0) {
          alert("You can't add empty file");
          return prevent();
        } else if (newFile.size > 10000000) {
          alert("You can't add file exceeds 10MB");
          return prevent();
        }
        if (
          !(
            newFile.name.indexOf("pdf") !== -1 ||
            newFile.name.indexOf("doc") !== -1 ||
            newFile.name.indexOf("docx") !== -1 ||
            newFile.name.indexOf("txt") !== -1 ||
            newFile.name.indexOf("jpg") !== -1 ||
            newFile.name.indexOf("jpeg") !== -1 ||
            newFile.name.indexOf("msg") !== -1 ||
            newFile.name.indexOf("png") !== -1
          )
        ) {
          alert(
            "You can't attach unsupported file. \nSupport File : pdf,doc,docx,txt,jpg,jpeg,msg,png"
          );
          return prevent();
        }

        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
          return prevent();
        }
        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
          return prevent();
        }
      }
      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        newFile.blob = "";
        let URL = window.URL || window.webkitURL;
        if (URL && URL.createObjectURL) {
          newFile.blob = URL.createObjectURL(newFile.file);
        }
        newFile.thumb = "";
        if (newFile.blob && newFile.type.substr(0, 6) === "image/") {
          newFile.thumb = newFile.blob;
        }
      }
    },
    inputFile(newFile, oldFile) {
      const file = !newFile ? false : newFile.file;
      function readFile(file, cb) {
        let FR = new FileReader();
        FR.readAsDataURL(file);
        FR.onloadend = () => {
          cb(FR.result);
        };
      }
      if (file) {
        new Compressor(file, {
          success(result) {
            readFile(result, (cb) => {
              newFile.base64 = cb;
            });
          },
          error(err) {
            readFile(file, (cb) => {
              newFile.base64 = cb;
            });
          },
        });
      }
    },
    showDeletePopup(list, item) {
      this.dialog = true;
      this.toRemoveItem = item;
      this.toRemoveList = list;
    },
    removeItem() {
      this.removeItemFromList(this.toRemoveList);
      this.dialog = false;
      this.toRemoveItem = null;
      this.toRemoveList = null;
    },
    removeItemFromList(list) {
      let self = this;
      const idx = list.findIndex(function(item) {
        return item.id === self.toRemoveItem.id;
      });
      if (idx > -1) list.splice(idx, 1);
      if (list.length === 0) {
        for (var i = 0, len = parent.length; i < len; i++) {
          if (parent[i].photos && parent[i].photos.length === 0) {
            delete parent[i].photos;
          }
          if (
            parent[i].statementproof &&
            parent[i].statementproof.length === 0
          ) {
            delete parent[i].statementproof;
          }
        }
      }
    },
    getClaimRecord: function() {
      return _.cloneDeep(this.$store.getters.getClaimRecord);
    },
    changeTextBox: function() {
      this.claimRecord.declined_reason = null;
      this.claimRecord.cancel_reason = null;
    },
    clearAttachment: function(e) {
      if(e === 'N') {
        this.claimRecord.attachment = [];
        this.claimRecord.is_exist_claim_desc = null;
      }
    },
    trackPage: function() {
      const postData = this.$store.state.accessData;
      postData.url = window.location.href + "/ClaimRecord";
      PremiumApi.logAccess(postData, function(data) {
        console.log("Data sent with : " + JSON.stringify(postData));
      });
    },
  },
  mounted: function() {
    window.scrollTo(0, 0);
    this.trackPage();
  },
}; 
</script>
